import * as Oidc from 'oidc-client-ts';
import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from '@module/shared/routerCompat';
import { MUIButton, MUITextField } from '@abb/arcadia-ui-react';

import { AuthorizedUserService } from '../services/authorizedUserService';
import { DbAuthService } from '../services/dbAuthService';
import { Settings } from '../Settings';

export class LoginComponent extends React.Component<any, any> {
  constructor(props: any) {
    super(props);
    this.state = {
      email: '',
      password: '',
      error: '',
      isSubmitting: false,
      processingRedirect: false
    };
  }

  componentDidMount() {
    // When returning from the Microsoft sign-in redirect the response is carried back in the URL.
    // Authorization Code + PKCE returns ?code=...&state=... in the query string; an error or the
    // legacy implicit flow returns data in the hash. Handle both so the callback always completes.
    const search = this.props.location.search || '';
    const hash = this.props.location.hash || '';
    const hasCodeResponse = /[?&](code|error)=/.test(search);
    const hasImplicitResponse = hash.length > 1;
    if (hasCodeResponse || hasImplicitResponse) {
      this.setState({ processingRedirect: true });
      const manager = new Oidc.UserManager(Settings);
      manager
        .signinRedirectCallback()
        .then((user) => this.props.login(user))
        .catch(() => {
          this.setState({ processingRedirect: false, error: 'Microsoft sign in could not be completed.' });
        });
    }
  }

  handleFieldChange = (field: 'email' | 'password', value: string) => {
    this.setState({ [field]: value, error: '' } as any);
  }

  handleDbLogin = async () => {
    if (this.state.isSubmitting) {
      return;
    }

    if (!this.state.email || !this.state.password) {
      this.setState({ error: 'Please enter your email and password.' });
      return;
    }

    this.setState({ isSubmitting: true, error: '' });

    try {
      const result = await DbAuthService.login(this.state.email, this.state.password);
      await this.props.login(result);
    } catch (error) {
      let message = 'Sign in failed.';
      if (error instanceof Error) {
        message = error.message;
      } else if (typeof error === 'string') {
        message = error;
      }
      this.setState({ isSubmitting: false, error: message });
    }
  }

  handleEntraLogin = () => {
    const manager = new Oidc.UserManager(Settings);
    manager.signinRedirect();
  }

  handleDbLoginSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    this.handleDbLogin();
  }

  handleFormKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.handleDbLogin();
    }
  }

  render() {
    if (this.state.processingRedirect) {
      return (
        <div style={styles.page}>
          <div style={styles.card}>
            <p style={styles.signingIn}>Signing you in&hellip;</p>
          </div>
        </div>
      );
    }

    const errorBanner = this.state.error
      ? <div style={styles.banner}>{this.state.error}</div>
      : null;
    const submitLabel = this.state.isSubmitting ? 'Signing in…' : 'Sign In';

    return (
      <div style={styles.page} className='login-page'>
        <div style={styles.card}>
          <div style={styles.accent} />
          <h1 style={styles.title}>License Portal</h1>
          <p style={styles.subtitle}>Sign in to continue</p>

          {errorBanner}

          <form onSubmit={this.handleDbLoginSubmit} onKeyDown={this.handleFormKeyDown}>
            <div style={styles.fieldWrap}>
              <MUITextField
                id='login-email'
                name='email'
                label='Email'
                placeholder='Email'
                type='email'
                size={32}
                textFieldWrapperClassName='login-text-field'
                value={this.state.email}
                onChange={(value) => this.handleFieldChange('email', value)}
              />
            </div>

            <div style={styles.fieldWrap}>
              <MUITextField
                id='login-password'
                name='password'
                label='Password'
                placeholder='Password'
                type='password'
                size={32}
                textFieldWrapperClassName='login-text-field'
                value={this.state.password}
                onChange={(value) => this.handleFieldChange('password', value)}
              />
            </div>

            <MUIButton
              id='dbLoginBtn'
              variant='primary'
              size={32}
              label={submitLabel}
              disabled={this.state.isSubmitting}
              onClickButton={this.handleDbLogin}
            />
          </form>

          <div style={styles.divider}>
            <span style={styles.dividerLine} />
            <span style={styles.dividerText}>or</span>
            <span style={styles.dividerLine} />
          </div>

          <MUIButton
            id='entraLoginBtn'
            variant='secondary'
            size={32}
            label='Sign in with Microsoft'
            onClickButton={this.handleEntraLogin}
          />
        </div>
      </div>
    );
  }
}

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: '64px',
    minHeight: 'calc(100vh - 48px)',
    backgroundImage: 'url(/abb_theme.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  },
  card: {
    position: 'relative',
    width: '360px',
    maxWidth: '92vw',
    padding: '32px',
    backgroundColor: '#ffffff',
    border: '1px solid #e6e6e6',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    margin: '50px auto'
  },
  accent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    backgroundColor: '#ff000f'
  },
  title: {
    margin: '8px 0 4px 0',
    fontSize: '24px',
    fontWeight: 600,
    color: '#1f1f1f'
  },
  subtitle: {
    margin: '0 0 24px 0',
    fontSize: '14px',
    color: '#696969'
  },
  banner: {
    marginBottom: '16px',
    padding: '10px 12px',
    fontSize: '13px',
    color: '#ffffff',
    backgroundColor: '#ff000f'
  },
  fieldWrap: {
    marginBottom: '16px'
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '20px 0'
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: '#e6e6e6'
  },
  dividerText: {
    margin: '0 12px',
    fontSize: '12px',
    color: '#aaaaaa',
    textTransform: 'uppercase'
  },
  signingIn: {
    textAlign: 'center',
    fontSize: '16px',
    color: '#464646'
  }
};

export default withRouter(connect(undefined, mapDispatchToProps)(LoginComponent) as any);

function mapDispatchToProps(dispatch: any, ownProps: any) {
  return {
    async login(user: any) {
      const profile = (user && user.profile) || {};
      const userEmail = profile.email || profile.preferred_username || profile.upn || profile.unique_name;
      if (!userEmail) {
        throw new Error('Unable to resolve user email from login response.');
      }

      const userPermissions = await AuthorizedUserService.getUserByEmail(userEmail, user);
      dispatch({ type: 'USER_LOGIN', loggedInUser: user, userPermissions });
      ownProps.history.push('/');
    }
  };
}
